import React, { useMemo } from 'react';
import '../../shared/style.css';
import { Button, Group, Dialog } from '@economic/taco';
import { useField } from 'react-final-form';
import { Field } from '../../../../../shared/components/Form';
import { t } from '../../../../../shared/components/l10n/l10n';
import { DisplayMessages } from '../../shared/components/DisplayMessages';
import { InvoicingMessageType, SendByType } from '../../invoices/book-and-send/types';
import { AutoCloseDrawer } from '../../shared/components/AutoCloseDrawer';
import { useRequiredFieldsValidation } from '../../shared/hooks/useRequiredFieldsValidation';
import { SalesDialogFormWithEmailOptionsDrawer } from '../../shared/components/SalesDialogFormWithEmailOptionsDrawer';
import { EmailTemplatePicker } from '../../invoices/utils/EmailTemplatePicker';
import { EmailTemplate } from '../../../../../../types/internal-api';
import { emailTemplateUpdater } from '../../invoices/utils/emailTemplateUpdater';
import { peppolIdUpdater } from '../../invoices/utils/peppolIdUpdater';

export interface SendInvoiceFormData {
    sendByType: SendByType;
    invoiceId: number;
    isCreditNote: boolean;
    canBeSentElectronically: boolean;
    hasBeenSentElectronically: boolean;
    emailOptions: {
        subject: string;
        body: string;
        encrypt: boolean;
        includePdfLink: boolean;
        includePdfAsAttachment: boolean;
        includeOioLink: boolean;
        includeOioAsAttachment: boolean;
        recipients: string;
        recipientsCc: string;
        recipientsBcc: string;
        saveSubject: boolean;
        saveBody: boolean;
    };
    templates: EmailTemplate[];
}

interface Props {
    loading: boolean;
    initialData?: SendInvoiceFormData;
    open: boolean;
    onSend: (values: any) => void;
    onHide: () => void;
}

export function SendInvoiceDialog(props: Props) {
    const { loading, initialData, open, onSend, onHide } = props;

    const updaters = useMemo(() => [...peppolIdUpdater(), emailTemplateUpdater(false)], []);

    return (
        <SalesDialogFormWithEmailOptionsDrawer
            loading={loading}
            loadingSpinnerWithText
            aria-label={t`FORM_ARCHIVESEND_INVOICE_HEADER`}
            open={open}
            onHide={onHide}
            className="book-and-send-dialog h-[500px]"
            size="xs"
            fullEmailDrawerAttachmentsOptions
            initialData={initialData}
            onSubmit={onSend}
            updates={updaters}>
            {(form, drawer) => (
                <>
                    <AutoCloseDrawer drawer={drawer} />
                    <SendInvoiceDialogContent initialData={initialData} onSubmit={() => form.submit()} onClose={onHide} />
                </>
            )}
        </SalesDialogFormWithEmailOptionsDrawer>
    );
}

interface SendInvoiceDialogContentProps {
    initialData?: SendInvoiceFormData;
    onSubmit: () => void;
    onClose: () => void;
}

function SendInvoiceDialogContent({ initialData, onSubmit, onClose }: SendInvoiceDialogContentProps) {
    const messages = useRequiredFieldsValidation();
    const {
        input: { value: sendByType },
    } = useField('sendByType');

    const canSubmit =
        sendByType !== SendByType.undefined && messages.filter(m => m.type === InvoicingMessageType.Error).length === 0;

    return (
        <div className="h-full flex flex-col">
            <Dialog.Title>
                {t(`FORM_ARCHIVESEND_INVOICE_HEADER`, {
                    DOCUMENT_TYPE:
                        initialData && initialData.isCreditNote
                            ? t`FORM_ARCHIVESEND_DOCUMENT_TYPE_CREDIT_NOTE`
                            : t`FORM_ARCHIVESEND_DOCUMENT_TYPE_INVOICE`,
                })}
            </Dialog.Title>

            <Field.NumberRadioGroup
                name="sendByType"
                options={[
                    {
                        text: t('FORM_ARCHIVESEND_SEND_BY_EMAIL'),
                        value: SendByType.email,
                    },
                    {
                        text: t('FORM_ARCHIVESEND_SEND_BY_EINVOICE'),
                        value: SendByType.oioxml,
                    },
                ]}
            />

            <EmailTemplatePicker templates={initialData!.templates} hidePicker={sendByType !== SendByType.email} />

            <div className="book-and-send-dialog__messages">
                <DisplayMessages messages={messages} />
            </div>

            <Dialog.Footer>
                <Group>
                    <Button appearance="discrete" onClick={onClose}>
                        {t('FORM_ARCHIVESEND_BUTTON_CANCEL')}
                    </Button>
                    <Button appearance="primary" type="submit" onClick={onSubmit} disabled={!canSubmit}>
                        {t('FORM_ARCHIVESEND_BUTTON_SEND')}
                    </Button>
                </Group>
            </Dialog.Footer>
        </div>
    );
}
